import { BaseController } from 'controllers/BaseController'
import { OnRequestChangeCallback } from 'services/APIs/BaseAPI'
import {
  GetProjectSummariesResponse,
  GetProjectSummariesResponsePaged,
  ProjectSummaryDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import {
  HttpGetMetaData,
  InternalAPI,
} from 'services/APIs/InternalAPI/InternalAPI'
import { GetPartyId } from 'store/storeUtils'

export interface IProjectsSummariesAPI {
  GetProjectsList(): Promise<GetProjectSummariesResponse>
  GetProjectsListPaged(
    onlyMyProjects?: boolean,
    meta?: HttpGetMetaData<ProjectSummaryDto>
  ): Promise<GetProjectSummariesResponsePaged>
  UpdateProjectStatus: (
    initialStatus: string,
    targetStatus: string,
    position: number,
    projectSummary: ProjectSummaryDto
  ) => Promise<void>
  UpdateProjectPriority: (projectId: string, priority: number) => Promise<void>
}

export class ProjectSummariesAPI
  extends InternalAPI
  implements IProjectsSummariesAPI
{
  constructor(
    partyId: string,
    private onRequestChange: OnRequestChangeCallback
  ) {
    super(`/api/parties/${partyId}/projects`)
  }

  public async GetProjectsList(): Promise<GetProjectSummariesResponse> {
    return await this.GetAsync<GetProjectSummariesResponse>({
      id: 'GetProjectsList',
      relativePath: '',
      onRequestChange: this.onRequestChange,
    })
  }

  public async GetProjectsListPaged(
    onlyMyProjects?: boolean,
    meta?: HttpGetMetaData<ProjectSummaryDto>
  ): Promise<GetProjectSummariesResponsePaged> {
    return await this.GetPagedAsync<GetProjectSummariesResponsePaged>({
      id: 'GetProjectsListPaged',
      relativePath: '/getProjectsPaged',
      onRequestChange: this.onRequestChange,
      _meta: meta,
      params: {
        onlyMyProjects: onlyMyProjects ?? false,
      },
    })
  }

  public async UpdateProjectStatus(
    initialStatus: string,
    targetStatus: string,
    position: number,
    projectSummary: ProjectSummaryDto
  ) {
    return await this.PutAsync<void>({
      id: `UpdateProjectStatus-${projectSummary.id}`,
      relativePath: `/${projectSummary.id}/status`,
      data: {
        initialStatus,
        targetStatus,
        position: position,
        projectSummary: projectSummary,
      },
      onRequestChange: this.onRequestChange,
    })
  }

  public async UpdateProjectPriority(projectId: string, priority: number) {
    return await this.PutAsync<void>({
      id: `UpdateProjectPriority-${projectId}`,
      relativePath: `/${projectId}/priority`,
      data: {
        priority: priority,
      },
      onRequestChange: this.onRequestChange,
    })
  }
}

export class ProjectsController extends BaseController<IProjectsSummariesAPI> {
  constructor() {
    super(
      (onRequestChanged) =>
        new ProjectSummariesAPI(GetPartyId(), onRequestChanged)
    )
  }

  public async GetProjectsList(): Promise<GetProjectSummariesResponse> {
    try {
      const response = await this.api.GetProjectsList()
      return response
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async GetProjectsListPaged(
    onlyMyProjects?: boolean,
    meta?: HttpGetMetaData<unknown>
  ): Promise<GetProjectSummariesResponsePaged> {
    try {
      const response = await this.api.GetProjectsListPaged(onlyMyProjects, meta)
      return response
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async UpdateProjectStatus(
    initialStatus: string,
    targetStatus: string,
    position: number,
    projectSummary: ProjectSummaryDto
  ) {
    try {
      await this.api.UpdateProjectStatus(
        initialStatus,
        targetStatus,
        position,
        projectSummary
      )
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async UpdateProjectPriority(projectId: string, priority: number) {
    try {
      await this.api.UpdateProjectPriority(projectId, priority)
    } catch (err) {
      throw this.HandleError(err)
    }
  }
}
